import type { UserSelf } from "storefront/User";
import type { AuthenticationData } from "storefront/components/Authentication/Data";
import { post } from "storefront/GrailedAPI/request";
import GrailedAPIError from "storefront/GrailedAPI/Error";
import {
  nextActionFromErrorBody,
  TwoFactorChallenge,
} from "storefront/GrailedAPI/NextAction";
import type { Credentials } from "./create";

type Response = {
  data: {
    redirectTo: string;
    token: string;
    user: UserSelf;
  };
};

export type VerifySignInFailure =
  | { type: "IncorrectCode"; challenge: TwoFactorChallenge }
  | { type: "ChallengeLocked"; challenge: TwoFactorChallenge }
  | { type: "SignInAgain" }
  | { type: "InvalidCode" }
  | { type: "Banned" }
  | { type: "TooManyRequests" }
  | { type: "Unknown" };

const DEFAULT_MESSAGES: Record<VerifySignInFailure["type"], string> = {
  IncorrectCode: "That code isn't right. Please try again.",
  ChallengeLocked: "Too many incorrect attempts. Please try again later.",
  SignInAgain: "Your verification code has expired. Please sign in again.",
  InvalidCode: "Please enter your verification code.",
  Banned: "That account is banned.",
  TooManyRequests: "Too many failed login requests. Please try again later.",
  Unknown:
    "Something went wrong, but we're not sure what. We suggest trying again or contacting help@grailed.com if the problem continues.",
};

export class VerifySignInError extends Error {
  failure: VerifySignInFailure;

  constructor(failure: VerifySignInFailure, message?: string) {
    super(message || DEFAULT_MESSAGES[failure.type]);
    this.name = "VerifySignInError";
    this.failure = failure;
  }
}

const failureFromReason = (reason: GrailedAPIError): VerifySignInFailure => {
  const nextAction = nextActionFromErrorBody(reason.body);

  if (nextAction?.type === "sign_in") {
    return { type: "SignInAgain" };
  }

  if (nextAction?.type === "two_factor_challenge") {
    return reason.response.status === 429
      ? { type: "ChallengeLocked", challenge: nextAction.twoFactorChallenge }
      : { type: "IncorrectCode", challenge: nextAction.twoFactorChallenge };
  }

  if (reason.response.status === 429) {
    return { type: "TooManyRequests" };
  }

  switch (reason.body.error?.error_id) {
    case "validation_error":
      return { type: "InvalidCode" };

    case "banned":
      return { type: "Banned" };

    case "forbidden":
      return { type: "SignInAgain" };

    default:
      return { type: "Unknown" };
  }
};

const errorFromReason = (reason: unknown): VerifySignInError => {
  if (!(reason instanceof GrailedAPIError)) {
    return new VerifySignInError({ type: "Unknown" });
  }

  const failure = failureFromReason(reason);
  const serverMessage =
    failure.type === "TooManyRequests" ? undefined : reason.body.error?.message;

  return new VerifySignInError(failure, serverMessage);
};

type VerifyParams = {
  challengeToken: string;
  code: string;
  credentials: Credentials;
};

/**
 * @name GrailedAPI.v1.Sessions.verify
 * @description Completes a sign in that returned `two_factor_required` by verifying the code that
 * was sent to the user. Resolves with the same AuthenticationData as a successful sign in, and
 * rejects with a VerifySignInError describing what the client should do next.
 */
const verify = async ({
  challengeToken,
  code,
  credentials,
}: VerifyParams): Promise<AuthenticationData> => {
  try {
    const response = await post<Response>("/api/sign_in/verify", {
      challengeToken,
      code,
    });

    return {
      redirectTo: response.data.redirectTo,
      token: response.data.token,
      user: response.data.user,
      credentials,
    };
  } catch (reason: unknown) {
    throw errorFromReason(reason);
  }
};

export default verify;
